import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface DirectoryUser {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  department: string;
  title: string;
  status: 'active' | 'inactive';
  lastLogin: string;
  provider: string;
  customer: string;
  groups: string[];
  externalId: string;
  managerEmail?: string;
  attributes: Record<string, any>;
  syncedAt: string;
}

export const useDirectoryUsers = () => {
  const [users, setUsers] = useState<DirectoryUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from('device_users')
          .select(`
            id,
            username,
            email,
            first_name,
            last_name,
            department,
            title,
            is_active,
            last_login,
            external_id,
            manager_email,
            groups,
            attributes,
            synced_at,
            identity_provider_id,
            identity_providers (
              name,
              type
            )
          `)
          .order('created_at', { ascending: false });

        if (error) throw error;

        const transformedUsers: DirectoryUser[] = (data || []).map((user: any) => ({
          id: user.id,
          username: user.username,
          email: user.email || '',
          firstName: user.first_name || '',
          lastName: user.last_name || '',
          department: user.department || '',
          title: user.title || '',
          status: user.is_active ? 'active' : 'inactive',
          lastLogin: user.last_login || new Date().toISOString(),
          provider: user.identity_providers?.name || 'Unknown',
          customer: user.email?.split('@')[1]?.split('.')[0] || 'Unknown',
          groups: user.groups || [],
          externalId: user.external_id,
          managerEmail: user.manager_email,
          attributes: user.attributes || {},
          syncedAt: user.synced_at,
        }));

        setUsers(transformedUsers);
        setError(null);
      } catch (err) {
        console.error('Error fetching directory users:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch users');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, []);

  return { users, isLoading, error };
};
